import React, { useEffect, useState, useRef } from 'react';
import { useParams, useLocation } from 'react-router';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
// TODO: DicomMetadataStore should be injected?
import {
  CommandsManager,
  DicomMetadataStore,
  ExtensionManager,
  HotkeysManager,
  ServicesManager,
  utils,
} from '@ohif/core';
import {
  DragAndDropProvider,
  ImageViewerProvider,
  useUserAuthentication,
} from '@ohif/ui';
import { useQuery, useSearchParams } from '@hooks';
import ViewportGrid from '@components/ViewportGrid';
import Compose from './Compose';
import {
  getRelatedParams,
  transformReport,
  transformStudy,
} from '../../utils/helpers';
import {
  fetchConsultationStudies,
  fetchRelatedStudies,
} from '../../services/auth.service';
import cookie from 'js-cookie';
import { IWebApiDataSource } from '@ohif/core';
import { PROTOCOL } from '@ohif/core/src/types/HangingProtocol';
import { Mode } from 'interfaces/global/global';
import { LoadingIndicatorProgress } from '@vindr/ui';
import { isConsultationMode, isPathologyMode } from '@vindr/utils/helpers';

const { formatDate } = utils;
enum GENDER {
  Male,
  Female,
  Other,
}
const FORMAT_DATE_VI = 'DD/MM/YYYY';
const FORMAT_DATE_EN = 'MM/DD/YYYY';

/**
 * Initialize the route.
 *
 * @param props.servicesManager to read services from
 * @param props.studyInstanceUIDs for a list of studies to read
 * @param props.dataSource to read the data from
 * @param props.filters filters from query params to read the data from
 * @returns array of subscriptions to cancel
 */
function defaultRouteInit(
  {
    servicesManager,
    studyInstanceUIDs,
    dataSource,
    filters,
    studyList,
  }: {
    servicesManager: ServicesManager;
    studyInstanceUIDs: string[];
    dataSource: Parameters<typeof IWebApiDataSource['create']>[0];
    filters: Record<string, any>;
    studyList: any[];
  },
  hangingProtocolId: Array<keyof typeof PROTOCOL>
) {
  const {
    displaySetService,
    hangingProtocolService,
    httpClientService,
    toolbarService,
  } = servicesManager.services;

  const unsubscriptions = [];
  if (studyInstanceUIDs[0] === '') {
    return unsubscriptions;
  }
  const { unsubscribe: instanceAddedUnsubscribe } =
    DicomMetadataStore.subscribe(
      DicomMetadataStore.EVENTS.INSTANCES_ADDED,
      function ({ StudyInstanceUID, SeriesInstanceUID, madeInClient = false }) {
        const seriesMetadata = DicomMetadataStore.getSeries(
          StudyInstanceUID,
          SeriesInstanceUID
        );

        displaySetService.makeDisplaySets(
          seriesMetadata.instances,
          madeInClient
        );
      }
    );

  unsubscriptions.push(instanceAddedUnsubscribe);

  const allRetrieves = studyInstanceUIDs.map(StudyInstanceUID =>
    dataSource.retrieve.series.metadata({
      StudyInstanceUID,
      filters,
    })
  );

  const allReports = studyInstanceUIDs.map(async StudyInstanceUID => {
    if (
      !StudyInstanceUID ||
      StudyInstanceUID === 'NOT_FOUND' ||
      StudyInstanceUID === 'EXPIRED'
    )
      return;
    let data;
    if (isPathologyMode()) {
      data = [];
    } else {
      const response = await httpClientService.getReport(StudyInstanceUID);
      data = response.data;
    }
    if (!data?.length) {
      displaySetService.makeEmptyDisplaySet(
        [transformStudy({}, StudyInstanceUID)],
        true
      );
      return;
    }
    let latestReports;
    if (StudyInstanceUID.startsWith('STUDY') && data?.length > 1) {
      // Fake study_instance_uid so we should get only the last report
      latestReports = data.sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      )[0];
    } else {
      latestReports = data;
    }
    return { StudyInstanceUID, latestReports };
  });

  const processDisplaySets = reports =>
    Promise.allSettled(allRetrieves).then(async () => {
      // add report displayset
      studyInstanceUIDs.forEach(async StudyInstanceUID => {
        if (
          !StudyInstanceUID ||
          StudyInstanceUID === 'NOT_FOUND' ||
          StudyInstanceUID === 'EXPIRED'
        )
          return;

        const latestReports = reports.find(
          it => it?.StudyInstanceUID === StudyInstanceUID
        )?.latestReports;
        if (!latestReports) {
          displaySetService.makeEmptyDisplaySet(
            [transformStudy({}, StudyInstanceUID)],
            true
          );
          return;
        }
        const study = studyList?.find(
          it => it.studyInstanceUid === StudyInstanceUID
        );

        latestReports.forEach(latestReport =>
          displaySetService.makeReportDisplaySet(
            [transformReport(latestReport, study)],
            true
          )
        );
      });
      const displaySets = displaySetService.getActiveDisplaySets();

      if (!displaySets || !displaySets.length) {
        return;
      }

      const studyMap = {};

      // Prior studies don't quite work properly yet, but the studies list
      // is at least being generated and passed in.
      const studies = displaySets.reduce((prev, curr) => {
        const { StudyInstanceUID } = curr;
        if (!studyMap[StudyInstanceUID]) {
          const study = DicomMetadataStore.getStudy(StudyInstanceUID);
          studyMap[StudyInstanceUID] = study || { StudyInstanceUID };
          prev.push(study || { StudyInstanceUID });
        }
        return prev;
      }, []);

      // The assumption is that the display set at position 0 is the first
      // study being displayed, and is thus the "active" study.
      const activeStudy = studies[0];

      // run the hanging protocol matching on the displaySets with the predefined
      // hanging protocol in the mode configuration
      hangingProtocolService.run(
        { studies, activeStudy, displaySets },
        hangingProtocolId
      );
    });

  // The hanging protocol matching service is fairly expensive to run multiple
  // times, and doesn't allow partial matches to be made (it will simply fail
  // to display anything if a required match fails), so we wait here until all metadata
  // is retrieved (which will synchronously trigger the display set creation)
  // until we run the hanging protocol matching service.
  Promise.all(allReports)
    .then(reports => {
      processDisplaySets(reports);
    })
    .catch(err => {
    processDisplaySets([]);
  });

  return unsubscriptions;
}

export default function ModeRoute({
  mode,
  dataSourceName,
  extensionManager,
  servicesManager,
  commandsManager,
  hotkeysManager,
}: {
  mode: Mode;
  dataSourceName?: string;
  extensionManager?: ExtensionManager;
  servicesManager?: ServicesManager;
  commandsManager?: CommandsManager;
  hotkeysManager?: HotkeysManager;
}) {
  // Parse route params/querystring
  const location = useLocation();
  const query = useQuery();
  const params = useParams();
  const searchParams = useSearchParams();
  const [{ user }] = useUserAuthentication();

  const [studyInstanceUIDs, setStudyInstanceUIDs] = useState<string[]>();
  const [studyList, setStudyList] = useState<any[]>();

  const [refresh, setRefresh] = useState(false);
  const layoutTemplateData = useRef(false);
  const locationRef = useRef(null);
  const isMounted = useRef(false);
  const { t } = useTranslation('Vindoc');

  if (location !== locationRef.current) {
    layoutTemplateData.current = null;
    locationRef.current = location;
  }

  const {
    displaySetService,
    hangingProtocolService,
    userAuthenticationService,
  } = servicesManager.services;

  const { extensions, sopClassHandlers, hotkeys: hotkeyObj } = mode;

  const runTimeHangingProtocolId = searchParams.get('hangingprotocolid');
  const token = searchParams.get('token');

  if (token) {
    // if a token is passed in, set the userAuthenticationService to use it
    // for the Authorization header for all requests
    userAuthenticationService.setServiceImplementation({
      getAuthorizationHeader: () => ({
        Authorization: 'Bearer ' + token,
      }),
    });

    // Create a URL object with the current location
    const urlObj = new URL(
      window.location.origin + location.pathname + location.search
    );

    // Remove the token from the URL object
    urlObj.searchParams.delete('token');
    const cleanUrl = urlObj.toString();

    // Update the browser's history without the token
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, '', cleanUrl);
    }
  }

  // Preserve the old array interface for hotkeys
  const hotkeys = Array.isArray(hotkeyObj) ? hotkeyObj : hotkeyObj?.hotkeys;
  const hotkeyName = hotkeyObj?.name || 'hotkey-definitions-v2';

  if (dataSourceName === undefined) {
    dataSourceName = extensionManager.defaultDataSourceName;
  }

  extensionManager.setActiveDataSource(dataSourceName);

  const dataSources = extensionManager.getActiveDataSource();

  // Only handling one instance of the datasource type (E.g. one DICOMWeb server)
  const dataSource = dataSources[0];
  // Only handling one route per mode for now
  const route = mode.routes[0];

  // For each extension, look up their context modules
  // TODO: move to extension manager.
  let contextModules = [];

  Object.keys(extensions).forEach(extensionId => {
    const allRegisteredModuleIds = Object.keys(extensionManager.modulesMap);
    const moduleIds = allRegisteredModuleIds.filter(id =>
      id.includes(`${extensionId}.contextModule.`)
    );

    if (!moduleIds || !moduleIds.length) {
      return;
    }

    const modules = moduleIds.map(extensionManager.getModuleEntry);
    contextModules = contextModules.concat(modules);
  });

  const contextModuleProviders = contextModules.map(a => a.provider);
  const CombinedContextProvider = ({ children }) =>
    Compose({ components: contextModuleProviders, children });

  function ViewportGridWithDataSource(props) {
    return ViewportGrid({ ...props, dataSource });
  }

  useEffect(() => {
    // Preventing state update for unmounted component
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    // Todo: this should not be here, data source should not care about params
    const initializeDataSource = async (params, query) => {
      let studyInstanceUIDs: any = [];
      const queryStudyInstanceUIDs = query.getAll('StudyInstanceUIDs') || [];

      try {
        // if (queryStudyInstanceUIDs.length) {
        // studyInstanceUIDs = await dataSource.initialize({
        //   params,
        //   query,
        // });
        // } else if (!window.location.pathname.includes('/dicomlocal')) {
        // get related studies
        let data;
        const relatedInfo = user?.relatedInfo;
        const relatedParams = getRelatedParams(query); // if relatedParams appear but not the relatedInfo, that mean we need to wait

        if (query.get('auth_type') === 'encrypted' && !user?.relatedInfo) {
          // Return because we need get hash info before get study list
          return;
        }
        if (isConsultationMode()) {
          const meetingId = query.get('id');
          data = await fetchConsultationStudies(
            meetingId,
            userAuthenticationService.getAuthorizationHeader()
          );
        } else if (queryStudyInstanceUIDs?.length) {
          let params = `study_instance_uid=${query.get('StudyInstanceUIDs')}`;
          if (query.get('access_key')) {
            params += `&only_main_study=true`;
          }
          if (
            Object.keys(userAuthenticationService.getAuthorizationHeader())
              .length
          ) {
            data = await fetchRelatedStudies(
              params,
              userAuthenticationService.getAuthorizationHeader(),
              true
            );
          } else {
            data = null;
          }
        } else if (relatedInfo) {
          cookie.set('X-PARAMS', relatedInfo);
          data = await fetchRelatedStudies(relatedInfo, null, false);
        } else if (relatedParams) {
          console.log('Need show loading');
          return;
        } else {
          setStudyInstanceUIDs(['NOT_FOUND']);
          return;
        }

        if (
          data?.error === 'EMSG_14006 Share study has been expired' ||
          data?.error_code === 'EMSG_14006'
        ) {
          setStudyInstanceUIDs(['EXPIRED']);
          return;
        } else if (!data?.data?.length) {
          setStudyInstanceUIDs(['NOT_FOUND']);
          return;
        }

        const studyList = (data?.data || []).map(it => {
          return {
            studyInstanceUid:
              it.study_instance_uid ||
              `STUDY:${it.order_group[0]}:${it.patient_id}`,
            date:
              it.study_date &&
              formatDate(
                it.study_date,
                `${
                  localStorage.getItem('i18nextLng') === 'vi'
                    ? FORMAT_DATE_VI
                    : FORMAT_DATE_EN
                } HH:mm:ss`
              ),
            description: it.study_desc,
            modalities: it.modality,
            numInstances: it.instances_count,
            patientName: it.patient_name,
            patientSex: t(GENDER[it.patient_sex]),
            patientDoB:
              it.patient_birth_date &&
              formatDate(
                it.patient_birth_date,
                localStorage.getItem('i18nextLng') === 'vi'
                  ? FORMAT_DATE_VI
                  : FORMAT_DATE_EN
              ),
            patientId: it.patient_id,
            visitId: it?.visit_id,
            procedureCode: it?.procedure_code,
            orderGroup: it?.order_group,
          };
        });

        setStudyList(studyList);

        studyInstanceUIDs = [
          studyList?.length
            ? studyList.map(it => it.studyInstanceUid)[0]
            : studyInstanceUIDs?.length
            ? studyInstanceUIDs[0]
            : 'NOT_FOUND',
        ];

        setStudyInstanceUIDs(studyInstanceUIDs);
      } catch (error) {
        setStudyInstanceUIDs(['NOT_FOUND']);
      }
    };

    initializeDataSource(params, query);
    return () => {
      layoutTemplateData.current = null;
    };
  }, [user]);

  useEffect(() => {
    const retrieveLayoutData = async () => {
      const layoutData = await route.layoutTemplate({
        location,
        servicesManager,
        studyInstanceUIDs,
      });
      if (isMounted.current) {
        layoutTemplateData.current = layoutData;
        setRefresh(!refresh);
      }
    };
    if (studyInstanceUIDs?.length && studyInstanceUIDs[0] !== undefined) {
      retrieveLayoutData();
    }
    return () => {
      layoutTemplateData.current = null;
    };
  }, [studyInstanceUIDs]);

  useEffect(() => {
    if (!hotkeys) {
      return;
    }

    hotkeysManager.setDefaultHotKeys(hotkeys);

    const userPreferredHotkeys = JSON.parse(
      localStorage.getItem('hotkey-definitions')
    );

    if (userPreferredHotkeys?.length) {
      hotkeysManager.setHotkeys(userPreferredHotkeys);
    } else {
      hotkeysManager.setHotkeys(hotkeys);
    }

    return () => {
      hotkeysManager.destroy();
    };
  }, []);

  useEffect(() => {
    if (!layoutTemplateData.current) {
      return;
    }

    // TODO: For some reason this is running before the Providers
    // are calling setServiceImplementation
    // TODO -> iterate through services.

    // Extension

    // Add SOPClassHandlers to a new SOPClassManager.
    displaySetService.init(extensionManager, sopClassHandlers);

    extensionManager.onModeEnter({
      servicesManager,
      extensionManager,
      commandsManager,
    });

    // use the URL hangingProtocolId if it exists, otherwise use the one
    // defined in the mode configuration
    /* const hangingProtocolIdToUse = hangingProtocolService.getProtocolById(runTimeHangingProtocolId)
    ? runTimeHangingProtocolId
    : hangingProtocol; */
    const hangingProtocolIdToUse = Array.from(
      hangingProtocolService.protocols.keys()
    );

    // Sets the active hanging protocols - if hangingProtocol is undefined,
    // resets to default.  Done before the onModeEnter to allow the onModeEnter
    // to perform custom hanging protocol actions
    hangingProtocolService.setActiveProtocolIds(hangingProtocolIdToUse);

    mode?.onModeEnter({
      servicesManager,
      extensionManager,
      commandsManager,
    });

    const setupRouteInit = async () => {
      /**
       * The next line should get all the query parameters provided by the URL
       * - except the StudyInstanceUIDs - and create an object called filters
       * used to filtering the study as the user wants otherwise it will return
       * a empty object.
       *
       * Example:
       * const filters = {
       *   seriesInstanceUID: 1.2.276.0.7230010.3.1.3.1791068887.5412.1620253993.114611
       * }
       */
      const filters =
        Array.from(query.keys()).reduce(
          (acc: Record<string, string>, val: string) => {
            if (val !== 'StudyInstanceUIDs') {
              if (['seriesInstanceUID', 'SeriesInstanceUID'].includes(val)) {
                return {
                  ...acc,
                  seriesInstanceUID: query.get(val),
                };
              }

              return { ...acc, [val]: query.get(val) };
            }
          },
          {}
        ) ?? {};

      if (route.init) {
        return await route.init(
          {
            servicesManager,
            extensionManager,
            hotkeysManager,
            studyInstanceUIDs,
            dataSource,
            filters,
          },
          hangingProtocolIdToUse
        );
      }

      return defaultRouteInit(
        {
          servicesManager,
          studyInstanceUIDs,
          studyList,
          dataSource,
          filters,
        },
        hangingProtocolIdToUse
      );
    };

    let unsubscriptions;
    setupRouteInit().then(unsubs => {
      unsubscriptions = unsubs;
    });

    return () => {
      // The mode.onModeExit must be done first to allow it to store
      // information, and must be in a try/catch to ensure subscriptions
      // are unsubscribed.
      try {
        mode?.onModeExit?.({
          servicesManager,
          extensionManager,
        });
      } catch (e) {
        console.warn('mode exit failure', e);
      }
      // The unsubscriptions must occur before the extension onModeExit
      // in order to prevent exceptions during cleanup caused by spurious events
      unsubscriptions.forEach(unsub => {
        unsub();
      });
      // The extension manager must be called after the mode, this is
      // expected to cleanup the state to a standard setup.
      extensionManager.onModeExit();
    };
  }, [
    mode,
    dataSourceName,
    location,
    route,
    servicesManager,
    extensionManager,
    hotkeysManager,
    studyInstanceUIDs,
    refresh,
  ]);

  const renderLayoutData = props => {
    const layoutTemplateModuleEntry = extensionManager.getModuleEntry(
      layoutTemplateData.current.id
    );
    const LayoutComponent = layoutTemplateModuleEntry.component;

    return <LayoutComponent {...props} />;
  };

  return (
    <ImageViewerProvider
      // initialState={{ StudyInstanceUIDs: StudyInstanceUIDs }}
      StudyInstanceUIDs={studyInstanceUIDs}
      StudyList={studyList}
      // reducer={reducer}
    >
      <CombinedContextProvider>
        <DragAndDropProvider>
          {layoutTemplateData.current &&
          studyInstanceUIDs?.length &&
          studyInstanceUIDs[0] !== undefined ? (
            renderLayoutData({
              ...layoutTemplateData.current.props,
              ViewportGridComp: ViewportGridWithDataSource,
            })
          ) : (
            <LoadingIndicatorProgress className="h-full w-full bg-black" />
          )}
        </DragAndDropProvider>
      </CombinedContextProvider>
    </ImageViewerProvider>
  );
}

ModeRoute.propTypes = {
  mode: PropTypes.object.isRequired,
  dataSourceName: PropTypes.string,
  extensionManager: PropTypes.object,
  servicesManager: PropTypes.object,
  hotkeysManager: PropTypes.object,
};
